<template>
    <div class="relative">
        <slot name="menu-bar"></slot>
        <div :id="elementId" :style="{ height }"></div>
        <input v-if="formElementName" type="hidden" :value="hiddenValue" :name="formElementName" />
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

import Quill from 'quill';

/**
 * Quill Modules
 */
import { ImageDrop } from 'quill-image-drop-module';
import BlotFormatter from 'quill-blot-formatter';
import MagicUrl from 'quill-magic-url';
import QuillMention from 'quill-mention';

Quill.register('modules/imageDrop', ImageDrop);
Quill.register('modules/blotFormatter', BlotFormatter);
Quill.register('modules/magicUrl', MagicUrl);
Quill.register('modules/mention', QuillMention);

// import 'quill/dist/quill.snow.css'
// import 'quill-mention/dist/quill.mention.css';

const props = defineProps({
    content: {
        type: String,
        default: '',
        required: false
    },
    formElementName: {
        type: String,
        required: false,
    },
    height: {
        type: String,
        default: '10rem',
        required: false
    },
    symbols: Array,
    modelValue: [Object, Array, String],
});

const emit = defineEmits(['update:modelValue']);

const quillInstance = ref(null);
const hiddenValue = ref(props.content);
const elementId = ref(`quill-container-${Math.random().toString(36).substr(2, 9)}`);

const gUpdate = () => {
    const value = quillInstance.value.root.innerHTML;
    const updatedContent = transformContent(value);
    hiddenValue.value = updatedContent;
    emit('update:modelValue', updatedContent);
};

// const handleSelect = (item, insertFunction) => {
//     tags.value.push(item.id);

//     const range = quillInstance.value.getSelection();

//     // Exit if there's no selection or the editor is empty
//     if (!range || quillInstance.value.getLength() === 1) {
//         return;
//     }

//     // Calculate the correct position to start inserting
//     const insertPosition = range.index > 0 ? range.index - 1 : 0;

//     // Perform deletion and insertion inside a batch operation
//     quillInstance.value.updateContents([
//         { retain: insertPosition },       // Move to the correct position
//         { delete: 1 }
//     ], Quill.sources.SILENT);

//     // Perform deletion and insertion inside a batch operation
//     quillInstance.value.insertText(insertPosition, item.id, {
//         link: item.link,
//         target: '_blank',
//     });

//     // Wait for the editor to update before setting the cursor
//     setTimeout(() => {
//         const newPosition = insertPosition + item.id.length + 1; // +1 for the space
//         if (newPosition <= quillInstance.value.getLength()) {
//             quillInstance.value.setSelection(newPosition, 0, Quill.sources.SILENT);
//         }
//     }, 0);

//     // // insertFunction(item);
// };

function transformContent(htmlContent) {
    // Create a DOM parser to parse the HTML content
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    // Find all <span class="mention"> elements and replace them
    doc.querySelectorAll('span.mention').forEach(span => {
        const link = span.dataset.link;
        const displayText = span.dataset.id; // Or any other attribute you want to use
        const newElement = doc.createElement('a');
        newElement.href = link;
        newElement.target = '_blank';
        newElement.textContent = displayText;

        span.replaceWith(newElement);
    });

    return doc.body.innerHTML;
}

onMounted(() => {
    quillInstance.value = new Quill(`#${elementId.value}`, {
        modules: {
            imageDrop: true,
            blotFormatter: true,
            magicUrl: true,
            mention: {
                allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                mentionDenotationChars: ["#"],
                showDenotationChar: false,
                spaceAfterInsert: true,
                source: function (searchTerm, renderList) {
                    if (!props.symbols) return;
                    const values = props.symbols
                        .filter(symbol => symbol.symbol.toLowerCase().includes(searchTerm.toLowerCase()))
                        .slice(0, 5); // Limit to 5 items

                    const mapSymbols = values.map((item) => {
                        return {
                            id: item.label,
                            value: item.symbol,
                            // link: `https://www.barchart.com/quotes/${item.symbol}`
                            link: `https://www.commoditytrends.com/interactive-charts/${item.symbol}`
                        };
                    });
                    renderList(mapSymbols, searchTerm);
                },
                // onSelect: handleSelect,
            },
            toolbar: [
                ['bold', 'italic', 'underline'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                // [{ 'direction': 'rtl' }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'color': [] }, { 'background': [] }],
                // [{ 'font': [] }],
                [{ 'align': [] }],
                ['link', 'image', 'video']
            ]
        },
        theme: 'snow',
    });

    quillInstance.value.root.innerHTML = props.content;
    quillInstance.value.on('text-change', gUpdate);
});
</script>

<style type="scss">
.ql-mention-list-container {
  /* width: 170px; */
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background-color: #ffffff;
  box-shadow: 0 2px 12px 0 rgba(30, 30, 30, 0.08);
  z-index: 9001;
  overflow: auto;
}

.ql-mention-loading {
    font-size: 0.82rem;
    line-height: 1.2rem;
    padding: 0.35rem 0.5rem;
    vertical-align: middle;
}

.ql-mention-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.ql-mention-list-item {
    font-size: 0.82rem;
    line-height: 1.2rem;
    padding: 0.35rem 0.5rem;
    vertical-align: middle;
    cursor: pointer;
}

.ql-mention-list-item.disabled {
  cursor: auto;
}

.ql-mention-list-item.selected {
  background-color: #d3e1eb;
  text-decoration: none;
}

.mention {
  height: 24px;
  width: 65px;
  border-radius: 6px;
  background-color: #d3e1eb;
  padding: 3px 0;
  margin-right: 2px;
  user-select: all;
}

.mention > span {
  margin: 0 3px;
}

.ql-editor p {
    margin-bottom: 0.45rem;
}
</style>
